import { queryOptions, type QueryClient } from "@tanstack/react-query";

import {
  loadBoardAuthoringService,
  type AuthoringServiceContext,
  type DryRunServices,
  type DryRunReport,
  type DryRunOptions,
} from "@/shared/domain/boards/boardAuthoringService";
import type { ViewerControlChoices } from "@/shared/domain/boards/viewerControlChoices";

export interface BoardDryRunQueryInput {
  definition: unknown;
  orgId: string;
  services: DryRunServices;
  /** The client the dry run's queries go through; also the client this query lives in. */
  queryClient: QueryClient;
  /** For an update, the stored definition it was applied to (`DryRunOptions.storedDefinition`). */
  storedDefinition?: unknown;
  /** The values the viewer chose in the board's controls (`DryRunOptions.viewerChoices`). */
  viewerChoices?: ViewerControlChoices;
}

/**
 * The TanStack query for the authoring service's dry run of a definition,
 * shared by the confirmation cards (through `useBoardDryRun`) and the create
 * and update tools (through `ensureDryRunReport`), so approving a proposal
 * reuses the run its card made.
 *
 * The key holds the serialized definition, for an update the serialized
 * stored definition it was patched from, and the viewer's control choices,
 * sorted by control name. A card rendered again with the same proposal reads
 * the cached report; a different proposal, an update whose stored board
 * changed since the card's run, or a control the viewer changed since then
 * gets a run of its own. The report never goes stale and is not refetched
 * on window focus, so a card left open does not rerun every query and
 * headless chart on each tab switch.
 *
 * The query function never reads TanStack's `signal`: TanStack cancels a
 * fetch that read it once the fetch's last observer unmounts, and a
 * confirmation card unmounts routinely (StrictMode mounts it twice, and
 * opening the approval details remounts it), so its run would be thrown away
 * and started over. Each run gets its own signal that nothing aborts
 * instead. Only the authoring service's time limit ends a run early, and the
 * run of a card that unmounted completes into the cache, where the next
 * mount or the tool's `execute` reads it.
 */
export function boardDryRunQueryOptions({
  definition,
  orgId,
  services,
  queryClient,
  storedDefinition,
  viewerChoices,
}: BoardDryRunQueryInput) {
  return queryOptions({
    queryKey: [
      "boardData",
      "authoringDryRun",
      orgId,
      JSON.stringify(definition),
      storedDefinition === undefined ? null : JSON.stringify(storedDefinition),
      viewerChoices === undefined
        ? null
        : JSON.stringify(
            [...viewerChoices].sort(([a], [b]) => a.localeCompare(b)),
          ),
    ],
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async (): Promise<DryRunReport> => {
      const service = await loadBoardAuthoringService();
      return service.dryRun(
        definition,
        {
          orgId,
          services,
          queryClient,
          signal: new AbortController().signal,
        },
        { storedDefinition, viewerChoices },
      );
    },
  });
}

/**
 * The dry-run report for `definition` under `boardDryRunQueryOptions`'s key,
 * which the create and update tools read on approval: the confirmation
 * card's report when the cache holds one that passed, the card's run while
 * it is still in flight, and otherwise a new run cached under that key,
 * including after the card's run failed. `ctx` carries no signal, since the
 * query gives each run its own.
 *
 * A blocked report already in the cache is not reused; the dry run runs
 * again. The model sends a write again unchanged only when a failure says
 * the same dry run can pass when run again (a request that did not
 * complete, a stage that ran out of time), and the cached report would
 * return that failure without running anything.
 */
export function ensureDryRunReport(
  definition: unknown,
  ctx: Omit<AuthoringServiceContext, "signal">,
  options?: DryRunOptions,
): Promise<DryRunReport> {
  const query = boardDryRunQueryOptions({
    definition,
    orgId: ctx.orgId,
    services: ctx.services,
    queryClient: ctx.queryClient,
    storedDefinition: options?.storedDefinition,
    viewerChoices: options?.viewerChoices,
  });
  return ctx.queryClient.getQueryData(query.queryKey)?.status === "blocked"
    ? ctx.queryClient.fetchQuery({ ...query, staleTime: 0 })
    : ctx.queryClient.ensureQueryData(query);
}
